import { FC, memo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { IProfessional, IProfessionalSchedulesAvailable } from "../../api/getProfessionals";
import ThemeContext from "../../themes/context";
import { formatDate, getMonthOfYearName, incrementOnDate, transformDateToType } from "../../utils/date-util/dateUtils";
import ButtonSecondary from "../buttons/ButtonSecondary";
import ProfessionalSubEspecialty from "../common/ProfessionalSubEspecialty";
import { CipherIcon, ScheduleIcon, StarIcon } from "../Icons";
import getUserData from "../utils/getUserData";

const CardTimesProfessionalsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: normal;
	align-items: normal;
	align-content: normal;
	padding: 23px;
`;

const ProfessionalIdentificationWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: normal;
	align-items: normal;
	align-content: normal;
`;

const ProfessionalAvatar = styled.img`
	width: 80px;
	height: 80px;
	border-radius: 80px;
`;

const ProfessionalDataWrapper = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: normal;
	align-items: normal;
	align-content: normal;
	margin-left: 7px;
`;

const ProfessionalName = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: bold;
	font-size: 17px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: left;
`;

const BoardData = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: left;
`;

const ProfessionalStatsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: normal;
	align-items: normal;
	align-content: normal;
	margin-top: 7px;
	gap: 8px;
`;
const ProfessionalPriceWrapper = styled.div`
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 12.5px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: left;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: center;
	gap: 4px;
`;

const ProfessionalAssessmentWrapper = styled.div`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.yellowVitat || props.theme.colors.secondary};
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: center;
	gap: 4px;
`;

const ProfessionalNextDate = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 12.5px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "2f334e"};
	text-align: left;
`;

const OpenHoursWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: center;
	gap: 7px;
	button:not(:last-child) {
		overflow: visible !important;
		width: 100%;
		max-width: fit-content;
	}
	button:last-child {
		flex-basis: 100%;
		white-space: nowrap;
	}
	@media screen and (max-width: 360px) {
		button:nth-child(3):not(:last-child) {
			display: none;
		}
	}
`;

const ProfessionalScheduleInfo = styled.div`
	display: flex;
	flex-direction: column;
	padding: 8px 6px;
	gap: 8px;
`;

interface CardTimesProfessionalsProps {
	professional: IProfessional;
	onAvailableTimeClick: (availableSchedule: IProfessionalSchedulesAvailable) => void;
}

const CardTimesProfessionals: FC<CardTimesProfessionalsProps> = ({ professional, onAvailableTimeClick }) => {
	const theme = useContext(ThemeContext);
	const navigate = useNavigate();
	const { skipPayment } = getUserData();

	const handleNextAvailableDate = (nextAvailableDate: string): string => {
		const date = transformDateToType(nextAvailableDate);
		const day = date.day;
		const localeFormattedDate = `${day} de ${getMonthOfYearName(date)}`;
		const formattedDate = formatDate("yyyy-MM-dd", date);

		if (formattedDate === formatDate("yyyy-MM-dd")) return `Hoje, ${localeFormattedDate}`;
		if (formattedDate === formatDate("yyyy-MM-dd", incrementOnDate(1, "day"))) return `Amanhã, ${localeFormattedDate}`;
		return localeFormattedDate;
	};

	const handleOpenSchedule = () => {
		navigate(`/medicos/${professional.codigoProfissional}/agendamentos`);
	};

	const handleOpenProfessionalProfile = () => {
		navigate(`/medicos/${professional.codigoProfissional}`);
	};

	return (
		<CardTimesProfessionalsWrapper>
			<ProfessionalIdentificationWrapper onClick={handleOpenProfessionalProfile}>
				<ProfessionalAvatar src={professional.imagemProfissional} />
				<ProfessionalDataWrapper>
					<ProfessionalName>{professional.nomeProfissional}</ProfessionalName>
					<BoardData>
						{professional.especialidade}
						{professional.conselho && " - " + professional.conselho.toUpperCase()}
						{professional.numeroConselho && " " + professional.numeroConselho}
					</BoardData>
					<ProfessionalStatsWrapper>
						{!skipPayment && (
							<ProfessionalPriceWrapper>
								<CipherIcon size={16} color={theme.colors.primary} />
								{professional.horariosDisponiveis.length > 0 &&
									professional.horariosDisponiveis[0].valor.toLocaleString("pt-br", {
										style: "currency",
										currency: "BRL",
									})}
							</ProfessionalPriceWrapper>
						)}
						<ProfessionalAssessmentWrapper>
							{professional.avaliacao > 0 && (
								<>
									<StarIcon size={14} color={theme.colors.yellowVitat || "#6A4CAF"} />
									{professional.avaliacao.toFixed(1)}
								</>
							)}
						</ProfessionalAssessmentWrapper>
					</ProfessionalStatsWrapper>
				</ProfessionalDataWrapper>
			</ProfessionalIdentificationWrapper>
			<ProfessionalScheduleInfo>
				{professional.subEspecialidades && professional.subEspecialidades.length > 0 && (
					<ProfessionalSubEspecialty>Subespecialidades: {professional.subEspecialidades.join(", ")}</ProfessionalSubEspecialty>
				)}
				{professional.horariosDisponiveis && professional.horariosDisponiveis.length > 0 && (
					<ProfessionalNextDate>Próxima data: {handleNextAvailableDate(professional.horariosDisponiveis[0].horarioInicial)}</ProfessionalNextDate>
				)}
			</ProfessionalScheduleInfo>
			<OpenHoursWrapper>
				{professional.horariosDisponiveis
					// MULTIPROCEDIMENTO
					// Remover possíveis choques de horário
					.filter((value, index, self) => {
						return index === self.findIndex(valueToCompare => valueToCompare.horarioInicial === value.horarioInicial);
					})
					.slice(0, 3)
					.map((horario, index: number) => {
						const date = transformDateToType(horario.horarioInicial);
						const dateToCompare = transformDateToType(professional.horariosDisponiveis[0].horarioInicial);

						if (date.day !== dateToCompare.day) return;
						return (
							<ButtonSecondary
								onClick={async () => await onAvailableTimeClick(horario)}
								key={index}
								label={formatDate("HH:mm", horario.horarioInicial)}
							/>
						);
					})}
				<ButtonSecondary onClick={handleOpenSchedule} icon={<ScheduleIcon color={theme.colors.whiteVitat || "#2F334B"} size={14} />} label="Ver agenda" />
			</OpenHoursWrapper>
		</CardTimesProfessionalsWrapper>
	);
};

export default memo(CardTimesProfessionals);
